<script lang="ts">
  import type { Skill } from '$lib/api/types';
  import FormModal from '../common/modal/FormModal.svelte';
  import { createSkill, deleteSkill, updateSkill } from '$lib/api/courses';
  import Label from '../common/bootstrap/Label.svelte';
  import Input from '../common/bootstrap/Input.svelte';

  interface Props {
    courseId: number;
    categoryId: number;
    skill: Skill | null;
    onClose: () => void;
  }

  let { courseId, categoryId, skill, onClose }: Props = $props();

  let name = $state(skill?.name ?? '');
  let description = $state(skill?.description ?? '');
  let pointsValue = $state(skill?.pointsValue.toString() ?? '');

  let nameError: string | null = $state(null);
  let nameTouched = $state(false);
  let descriptionTouched = $state(false);
  let pointsValueTouched = $state(false);
</script>

<FormModal
  formId="skill_editor"
  invalidateUrl={url => url.pathname.startsWith('/api/courses/')}
  header="Skill"
  onDelete={skill
    ? async () => {
        await deleteSkill(courseId, categoryId, skill!.id);
      }
    : undefined}
  deleteConfirmText="Delete skill?"
  onclose={onClose}
  onsubmit={async () => {
    if (skill) {
      await updateSkill(courseId, categoryId, skill!.id, {
        name,
        description,
        pointsValue: parseInt(pointsValue),
      });
    } else {
      await createSkill(courseId, categoryId, {
        name,
        description,
        pointsValue: parseInt(pointsValue),
      });
    }
    return true;
  }}
  onvalidationerror={e => {
    if (e.errors.Name) {
      nameError = e.errors.Name.join(' ');
      return true;
    }
    return false;
  }}
  validationChecks={() => {
    nameTouched = true;
    descriptionTouched = true;
    pointsValueTouched = true;
    nameError = null;
    return name.length > 0 && name.length <= 256 && description.length <= 512 && pointsValue.length > 0;
  }}
>
  <div class="mb-3">
    <Label for="skill_name" class="is-required">Name</Label>
    <Input
      bind:value={name}
      required
      maxlength={256}
      type="text"
      id="skill_name"
      placeholder="Skill Name"
      invalid={nameTouched && (nameError !== null || name.length === 0 || name.length > 256)}
      onblur={() => (nameTouched = true)}
      feedback={nameError ?? 'Skill Name is required and must be 256 characters or less.'}
    />
  </div>
  <div class="mb-3">
    <Label for="skill_description">Description</Label>
    <Input
      bind:value={description}
      maxlength={512}
      type="textarea"
      id="skill_description"
      placeholder="Skill Description"
      invalid={descriptionTouched && description.length > 512}
      onblur={() => (descriptionTouched = true)}
      feedback="Skill Description must be 512 characters or less."
    />
  </div>
  <div class="mb-3">
    <Label for="skill_points_value" class="is-required">Points Value</Label>
    <Input
      value={pointsValue}
      oninput={e => {
        let newVal = e.currentTarget.value.replace(/\D/g, '');
        e.currentTarget.value = newVal;
        pointsValue = newVal;
      }}
      onfocus={e => {
        (e.currentTarget as HTMLInputElement).select();
      }}
      required
      inputmode="numeric"
      type="text"
      id="skill_points_value"
      placeholder="Points Value"
      invalid={pointsValueTouched && pointsValue.length === 0}
      onblur={() => (pointsValueTouched = true)}
      feedback="Points Value is required."
    />
  </div>
</FormModal>
